// Full-page sentiment map route (`/post/:postId/sentiment`) — opens from a
// story's map icon, renders the heat map full-bleed, closes back to the
// previous route. Real data via Apollo (Post.sentimentMap, TRIL-302). When a
// story has no substantial data, the SentimentMap view shows an inline
// "not enough data yet" message instead of an empty map.
import { useQuery } from '@apollo/client';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';

import { SentimentMap } from '../../../components/SentimentMap/SentimentMap';
import type { SentimentMap as SentimentMapData } from '../../../components/SentimentMap/types';
import { GET_SENTIMENT_MAP_QUERY } from '../../../graphql/resolvers/postQueries/getSentimentMap.gql';
import { detectSystemTheme } from '../../../helpers/theme';
import './sentiment-map-page.scss';

function usePrefersDark(): boolean {
  const [dark, setDark] = useState(() => detectSystemTheme() === 'dark');
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const fn = (e: MediaQueryListEvent) => setDark(e.matches);
    mq.addEventListener('change', fn);
    return () => mq.removeEventListener('change', fn);
  }, []);
  return dark;
}

export default function SentimentMapPage() {
  const { postId } = useParams();
  const navigate = useNavigate();
  const dark = usePrefersDark();

  // DEV-ONLY zoom-testing escape hatch: `?mock=1` renders the rich fixture so the
  // interaction can be felt inside the real route while demo data is sparse.
  // `import.meta.env.DEV`-gated AND dynamically imported so the fixture is dead
  // code in a prod build (never bundled, never reachable). Removed before Phase 1
  // is "done", along with the harness + fixture.
  const [searchParams] = useSearchParams();
  const useMock = import.meta.env.DEV && searchParams.get('mock') === '1';
  const [mock, setMock] = useState<SentimentMapData | null>(null);
  useEffect(() => {
    if (!useMock) return;
    let alive = true;
    import('../../../components/SentimentMap/__fixtures__/mockSentimentMap').then((m) => {
      if (alive) setMock(m.mockSentimentMap);
    });
    return () => {
      alive = false;
    };
  }, [useMock]);

  const { data, loading } = useQuery(GET_SENTIMENT_MAP_QUERY, {
    variables: { postId: postId ?? '' },
    skip: !postId || useMock,
  });

  // The generated result mirrors the hand-typed contract in components/SentimentMap/types.ts
  // (verified against the live schema). The cast is the seam until the component migrates
  // to codegen types directly.
  const sentimentMap = useMock
    ? mock
    : ((data?.getPostById?.sentimentMap ?? null) as unknown as SentimentMapData | null);

  return (
    <div className={`smap-page ${dark ? 'smap-page--dark' : 'smap-page--light'}`}>
      <button
        type="button"
        className="smap-page__close"
        onClick={() => navigate(-1)}
        aria-label="Close sentiment map"
      >
        ✕
      </button>
      <SentimentMap data={sentimentMap} loading={useMock ? mock == null : loading} dark={dark} />
    </div>
  );
}
